import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import Layout from './components/Layout.jsx';
const Dashboard = React.lazy(() => import('./pages/Dashboard.jsx'));
const Statistics = React.lazy(() => import('./pages/Statistics.jsx'));
const Users = React.lazy(() => import('./pages/Users.jsx'));
const UserProfile = React.lazy(() => import('./pages/UserProfile.jsx'));
const BroadcastManagerNew = React.lazy(() => import('./pages/BroadcastManagerNew.jsx'));
const BroadcastAnalytics = React.lazy(() => import('./pages/BroadcastAnalytics.jsx'));
const Messenger = React.lazy(() => import('./pages/Messenger.jsx'));
const Appeals = React.lazy(() => import('./pages/Appeals.jsx'));
const AppLogs = React.lazy(() => import('./pages/AppLogs.jsx'));
const AppErrors = React.lazy(() => import('./pages/AppErrors.jsx'));
const Updates = React.lazy(() => import('./pages/Updates.jsx'));
const AppsLayout = React.lazy(() => import('./pages/AppsLayout.jsx'));
const AppSessions = React.lazy(() => import('./pages/AppSessions.jsx'));
const AppConnections = React.lazy(() => import('./pages/AppConnections.jsx'));
// Страницы мобильной телеметрии — работа Максима, восстановлена вместе с AppsLayout.
const AppOnline = React.lazy(() => import('./pages/AppOnline.jsx'));
const AppFailures = React.lazy(() => import('./pages/AppFailures.jsx'));
const AppStats = React.lazy(() => import('./pages/AppStats.jsx'));
const Accounting = React.lazy(() => import('./pages/Accounting.jsx'));
const Settings = React.lazy(() => import('./pages/Settings.jsx'));
const Monitoring = React.lazy(() => import('./pages/Monitoring.jsx'));
const News = React.lazy(() => import('./pages/News.jsx'));
const Emergency = React.lazy(() => import('./pages/Emergency.jsx'));
// БИ-аналитика (когорты/BI) перенесена вкладками в раздел «Статистика»
// (Statistics.jsx импортирует их из pages/Analytics.jsx). Роут /analytics
// оставлен редиректом, чтобы старые ссылки не отваливались.
// Winback «Возврат неиспользованных дней»
const Winback = React.lazy(() => import('./pages/Winback.jsx'));
// Обзор бизнес-событий (analytics.events)
const Events = React.lazy(() => import('./pages/Events.jsx'));
import ProtectedRoute from './components/ProtectedRoute.jsx';
import LoginError from './components/LoginError.jsx';
const Login = React.lazy(() => import('./pages/Login.jsx'));

// Компонент для обработки ошибок аутентификации
function AppContent() {
  const { error, loading } = useAuth();

  // Если есть ошибка аутентификации, показываем страницу ошибки
  if (error && !loading) {
    return (
      <LoginError 
        error={error}
        onRetry={() => window.location.reload()}
        onGoHome={() => window.location.href = '/'}
      />
    );
  }

  return (
    <div className="App">
      <Suspense fallback={<div className="p-6">Загрузка...</div>}>
      <Routes>
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="users" element={<Users />} />
          <Route path="users/:id" element={<UserProfile />} />
          <Route path="messenger" element={<Messenger />} />
          <Route path="appeals" element={<Appeals />} />
          <Route path="traffic" element={<Navigate to="/monitoring?tab=servers" replace />} />
          <Route path="broadcasts" element={<BroadcastManagerNew />} />
          <Route path="broadcast-analytics" element={<BroadcastAnalytics />} />
          {/* Разделение по платформам — работа Максима. Была снесена деплоем
              15.07 (Actions делает git reset --hard, а страницы жили на сервере
              как untracked-дрифт). Восстановлено из sourcesContent в
              dist.bak.analytics/AppsLayout.js.map и внесено В РЕПОЗИТОРИЙ, чтобы
              следующий деплой её больше не стирал. */}
          <Route path="apps" element={<AppsLayout />}>
            <Route index element={<Navigate to="mobile/online" replace />} />

            <Route path="mobile" element={<Navigate to="/apps/mobile/online" replace />} />
            <Route path="mobile/online" element={<AppOnline />} />
            <Route path="mobile/failures" element={<AppFailures />} />
            <Route path="mobile/stats" element={<AppStats />} />
            <Route path="mobile/connections" element={<AppConnections />} />

            <Route path="desktop" element={<Navigate to="/apps/desktop/sessions" replace />} />
            <Route path="desktop/sessions" element={<AppSessions />} />
            <Route path="desktop/connections" element={<AppConnections />} />
            <Route path="desktop/logs" element={<AppLogs />} />
            <Route path="desktop/errors" element={<AppErrors />} />
            <Route path="desktop/updates" element={<Updates />} />

            {/* Старые плоские адреса — из закладок и внешних ссылок. */}
            <Route path="online" element={<Navigate to="/apps/mobile/online" replace />} />
            <Route path="failures" element={<Navigate to="/apps/mobile/failures" replace />} />
            <Route path="stats" element={<Navigate to="/apps/mobile/stats" replace />} />
            <Route path="sessions" element={<Navigate to="/apps/desktop/sessions" replace />} />
            <Route path="connections" element={<Navigate to="/apps/desktop/connections" replace />} />
            <Route path="logs" element={<Navigate to="/apps/desktop/logs" replace />} />
            <Route path="errors" element={<Navigate to="/apps/desktop/errors" replace />} />
            <Route path="updates" element={<Navigate to="/apps/desktop/updates" replace />} />
          </Route>
          <Route path="accounting" element={<Accounting />} />
          <Route path="monitoring" element={<Monitoring />} />
          <Route path="news" element={<News />} />
          <Route path="marzban" element={<Navigate to="/monitoring?tab=servers" replace />} />
          <Route path="emergency" element={<Emergency />} />
          <Route path="settings" element={<Settings />} />
          <Route path="app-logs" element={<Navigate to="/apps/desktop/logs" replace />} />
          <Route path="app-errors" element={<Navigate to="/apps/desktop/errors" replace />} />
          <Route path="updates" element={<Navigate to="/apps/desktop/updates" replace />} />
          <Route path="analytics" element={<Navigate to="/statistics" replace />} />
          <Route path="winback" element={<Winback />} />
          <Route path="events" element={<Events />} />
        </Route>
        <Route path="/login" element={
          <Suspense fallback={<div className="p-6">Загрузка...</div>}>
            <Login />
          </Suspense>
        } />
      </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
